import React, { useState, useEffect, useCallback } from "react";
import { ArrowRight, Crown, Calendar, FileText, Star } from "lucide-react";
import {
  membershipTiers as mockTiers,
  coachingSessions as mockCoaching,
  prepMaterials as mockPrep,
  memberTools as mockTools,
} from "../mock";
import { useToast } from "../hooks/use-toast";
import { getBLounge, postBooking, postMembership } from "../api";
import LeadDialog from "../components/LeadDialog";
import { useI18n } from "../i18n/I18nContext";
import RoiCalculator from "../components/blounge/RoiCalculator";
import CoachingCard from "../components/blounge/CoachingCard";
import PrepCard from "../components/blounge/PrepCard";
import ToolCard from "../components/blounge/ToolCard";
import TierCard from "../components/blounge/TierCard";

const SectionHeading = ({ icon: Icon, eyebrow, title }) => (
  <div className="flex items-center gap-3 mb-10">
    <Icon className="w-6 h-6 text-[#0a6ed1]" />
    <div>
      <span className="text-sm font-700 uppercase tracking-widest text-[#0a6ed1]">{eyebrow}</span>
      <h2 className="font-display font-800 text-2xl md:text-3xl text-[#0a2540]">{title}</h2>
    </div>
  </div>
);

const BLounge = () => {
  const { toast } = useToast();
  const { t, localize } = useI18n();
  const [rawTiers, setTiers] = useState(mockTiers);
  const [rawCoaching, setCoaching] = useState(mockCoaching);
  const [rawPrep, setPrep] = useState(mockPrep);
  const [rawTools, setTools] = useState(mockTools);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    let active = true;
    getBLounge()
      .then((d) => {
        if (!active || !d) return;
        if (d.tiers?.length) setTiers(d.tiers);
        if (d.coaching?.length) setCoaching(d.coaching);
        if (d.prep?.length) setPrep(d.prep);
        if (d.tools?.length) setTools(d.tools);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const tiers = localize("membership_tiers", rawTiers);
  const coaching = localize("coaching_sessions", rawCoaching);
  const prep = localize("prep_materials", rawPrep);
  const tools = localize("member_tools", rawTools);

  const book = useCallback(
    (session) =>
      setDialog({
        title: t("bookTitle"),
        description: t("bookDescTpl", { title: session.title, coach: session.coach }),
        cta: t("confirmBooking"),
        onSubmit: async ({ name, email }) => {
          try {
            await postBooking({ session_id: session.id, session_title: session.title, name, email });
            toast({ title: t("sessionSent"), description: t("sessionSentBodyTpl", { title: session.title }) });
          } catch (e) {
            toast({ title: t("bookingFailed"), description: t("tryMoment") });
          }
        },
      }),
    [toast, t]
  );

  const joinTier = useCallback(
    (tier) => {
      const isEnterprise = tier.price === "Custom";
      setDialog({
        title: isEnterprise ? t("enterpriseTitle") : t("joinTitleTpl", { name: tier.name }),
        description: isEnterprise
          ? t("enterpriseDesc")
          : t("joinDescTpl", { price: tier.price, cadence: tier.cadence }),
        cta: isEnterprise ? t("requestCall") : t("completeMembership"),
        onSubmit: async ({ name, email }) => {
          try {
            await postMembership({ tier_id: tier.id, tier_name: tier.name, name, email });
            toast(
              isEnterprise
                ? { title: t("requestReceived"), description: t("requestReceivedBody") }
                : { title: t("welcomeTpl", { name: tier.name }), description: t("membershipConfirmed") }
            );
          } catch (e) {
            toast({ title: t("somethingWrong"), description: t("tryAgain") });
          }
        },
      });
    },
    [toast, t]
  );

  const download = useCallback(
    (item) =>
      toast(
        item.premium
          ? { title: t("premiumContent"), description: t("premiumContentBodyTpl", { title: item.title }) }
          : { title: t("downloadStarted"), description: t("downloadStartedBodyTpl", { title: item.title }) }
      ),
    [toast, t]
  );

  const openTool = useCallback(
    (tool) =>
      toast(
        tool.premium
          ? { title: t("premiumTool"), description: t("premiumToolBodyTpl", { name: tool.name }) }
          : { title: t("launchingTool"), description: tool.name }
      ),
    [toast, t]
  );

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-[#0a2540] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-gradient-to-br from-[#0a6ed1] to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-600">
            <Crown className="w-4 h-4 text-[#c9a227]" /> {t("membersOnly")}
          </span>
          <h1 className="font-display font-800 text-4xl md:text-6xl mt-5 max-w-3xl leading-[1.05]">
            {t("theBlounge")}
          </h1>
          <p className="mt-5 text-slate-200 text-lg max-w-2xl leading-relaxed">
            {t("blHeroBody")}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#membership" className="inline-flex items-center gap-2 bg-white text-[#0a2540] hover:bg-slate-100 font-600 px-7 py-3.5 rounded-full transition-colors">
              {t("becomeMember")} <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#tools" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white font-600 px-7 py-3.5 rounded-full transition-colors">
              {t("exploreTools")}
            </a>
          </div>
        </div>
      </section>

      {/* Coaching sessions */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-20">
        <SectionHeading icon={Calendar} eyebrow={t("coachingLabel")} title={t("bookCoaching")} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {coaching.map((s) => (
            <CoachingCard key={s.id} session={s} onBook={book} />
          ))}
        </div>
      </section>

      {/* Preparation material */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <SectionHeading icon={FileText} eyebrow={t("resourcesLabel")} title={t("prepMaterial")} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {prep.map((m) => (
              <PrepCard key={m.id} item={m} onAction={download} />
            ))}
          </div>
        </div>
      </section>

      {/* Unique tools */}
      <section id="tools" className="max-w-[1400px] mx-auto px-5 md:px-8 py-20">
        <SectionHeading icon={Star} eyebrow={t("membersToolkit")} title={t("uniqueTools")} />
        <RoiCalculator />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {tools.map((tool) => (
            <ToolCard key={tool.id} tool={tool} onAction={openTool} />
          ))}
        </div>
      </section>

      {/* Membership tiers */}
      <section id="membership" className="bg-[#0a2540] text-white py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-sm font-700 uppercase tracking-widest text-[#5eb0f5]">{t("membershipLabel")}</span>
            <h2 className="font-display font-800 text-3xl md:text-4xl mt-2">{t("chooseAccess")}</h2>
            <p className="mt-4 text-slate-300">{t("membershipBody")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiers.map((tier) => (
              <TierCard key={tier.id} tier={tier} onJoin={joinTier} />
            ))}
          </div>
        </div>
      </section>

      <LeadDialog config={dialog} onOpenChange={setDialog} />
    </div>
  );
};

export default BLounge;
